/**
 * VAULT INSIGHTS — the central bank of contextual educational nudges.
 *
 * Each insight is a small piece of financial-organisation education.
 * They surface on public feature pages, inside the app (max ONE per
 * page), and at the end of Journal articles.
 *
 * Content rules:
 *   • NEVER personalised investment / tax / legal / credit advice.
 *   • NEVER references a specific security, ticker, or product.
 *   • Category maps to product surfaces so surfacing stays contextual.
 */
export const VAULT_INSIGHTS = [
    {
        id: "annual-subs-forgotten",
        category: "subscriptions",
        insight: "Annual subscriptions are often easier to forget because the charge appears only once a year.",
        why_it_matters: "The rhythm you don't feel is the one you don't manage. A yearly renewal breaks every mental model built on monthly reviews.",
        read_more_url: "/learn/articles/subscriptions",
        related_feature: "/subscriptions",
        reading_time: "2 min",
        cta: "Why recurring payments deserve a monthly review",
    },
    {
        id: "balance-vs-net-worth",
        category: "net_worth",
        insight: "Your bank balance is a snapshot. Your net worth is the picture.",
        why_it_matters: "Two households with identical bank balances can have very different financial positions once you count what they own and what they owe.",
        read_more_url: "/learn/articles/bank-balance-vs-net-worth",
        related_feature: "/net-worth",
        reading_time: "6 min",
        cta: "Understand net worth in plain English",
    },
    {
        id: "documents-findable",
        category: "documents",
        insight: "The value of a financial document is decided the day you need it — not the day you file it.",
        why_it_matters: "Most household documents are 'stored' somewhere. Very few are actually findable in the ninety seconds you're given during a real emergency.",
        read_more_url: "/learn/articles/family-vault-checklist",
        related_feature: "/documents",
        reading_time: "5 min",
        cta: "The five categories every family should be able to find",
    },
    {
        id: "together-not-merged",
        category: "together",
        insight: "A shared financial life does not require every financial record to become shared.",
        why_it_matters: "Financial transparency in a household is a spectrum, not a switch. Software that offers only 'all-shared' or 'all-private' forces couples into a choice that isn't real life.",
        read_more_url: "/learn/articles/couples-money-privacy",
        related_feature: "/together",
        reading_time: "7 min",
        cta: "Organise money together without losing privacy",
    },
    {
        id: "info-problem",
        category: "general",
        insight: "Most households don't have a money problem. They have an information problem.",
        why_it_matters: "The reason most people feel financially disorganised has almost nothing to do with numbers — and almost everything to do with information living in seven different places.",
        read_more_url: "/learn/articles/information-problem",
        related_feature: "/dashboard",
        reading_time: "6 min",
        cta: "The information problem hiding in most financial lives",
    },
    {
        id: "missed-payment-org",
        category: "payments",
        insight: "A missed payment is usually an organisation problem — not a money problem.",
        why_it_matters: "Very few households miss a payment because they can't pay. Most miss it because the date landed in an email nobody opened.",
        read_more_url: "/learn/articles/information-problem",
        related_feature: "/payments",
        reading_time: "3 min",
        cta: "Why financial calendars quietly prevent problems",
    },
];

export function insightsFor(category, limit = 1) {
    const pool = VAULT_INSIGHTS.filter((i) => i.category === category);
    return pool.slice(0, limit);
}

export function todayInsight() {
    // Deterministic-per-day rotation — same insight for the whole day
    // so a user doesn't see a different tile each refresh.
    const day = new Date();
    const idx = (day.getFullYear() * 372 + day.getMonth() * 31 + day.getDate())
                % VAULT_INSIGHTS.length;
    return VAULT_INSIGHTS[idx];
}
